import React from 'react';
import { UseImageUploadReturn } from '../../hooks/useImageUpload';

/**
 * Image Upload Field Component
 * 
 * Reusable UI component for image upload with preview, progress indicator,
 * and error display. Works with the useImageUpload hook.
 * 
 * Features:
 * - Profile image preview with circular avatar
 * - Upload button with loading state
 * - Remove image functionality
 * - Error display
 * - Accessible file input
 * 
 * @example
 * ```tsx
 * const imageUpload = useImageUpload({
 *   userId: user.id,
 *   isGuest: false,
 *   onSuccess: (url) => setFormData({ ...formData, profileImage: url })
 * });
 * 
 * <ImageUploadField
 *   imageUrl={formData.profileImage}
 *   displayName={formData.displayName}
 *   imageUpload={imageUpload}
 *   onRemove={() => setFormData({ ...formData, profileImage: '' })}
 * />
 * ```
 */

export interface ImageUploadFieldProps {
  /**
   * Current image URL to display
   */
  imageUrl?: string;
  
  /**
   * Display name for avatar fallback initial
   */
  displayName?: string;
  
  /**
   * Image upload hook instance (from useImageUpload)
   */
  imageUpload: Pick<UseImageUploadReturn, 'uploading' | 'error' | 'fileInputRef' | 'triggerFilePicker' | 'handleFileChange'>;
  
  /**
   * Callback when user clicks remove button
   */
  onRemove?: () => void;
  
  /**
   * Optional label text (default: "Profile Image")
   */
  label?: string;
  
  /**
   * Optional help text (default: "JPEG, PNG, GIF up to 5MB")
   */
  helpText?: string;
  
  /**
   * Optional custom size for avatar preview (default: 16 = 4rem/64px)
   */
  avatarSize?: number;
  
  /**
   * Optional custom button color (default: gray)
   */
  buttonColorClass?: string;
}

export function ImageUploadField({
  imageUrl,
  displayName,
  imageUpload,
  onRemove,
  label = 'Profile Image',
  helpText = 'JPEG, PNG, GIF up to 5MB',
  avatarSize = 16,
  buttonColorClass = 'focus:ring-indigo-500',
}: ImageUploadFieldProps) {
  const { uploading, error, fileInputRef, triggerFilePicker, handleFileChange } = imageUpload;
  
  const avatarSizeClass = `h-${avatarSize} w-${avatarSize}`;
  const fallbackInitial = displayName ? displayName.charAt(0).toUpperCase() : '?';
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex items-center space-x-4">
        {/* Avatar Preview */}
        <div className={`${avatarSizeClass} rounded-full overflow-hidden bg-gray-100 dark:bg-neutral-800 flex-shrink-0`}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Profile preview"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gray-200 dark:bg-slate-600">
              <span className="text-gray-400 dark:text-gray-500 text-xl font-medium">
                {fallbackInitial}
              </span>
            </div>
          )}
        </div>
        
        {/* Upload Controls */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={triggerFilePicker}
            disabled={uploading}
            className={`inline-flex items-center px-3 py-2 border border-gray-300 dark:border-neutral-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:ring-offset-slate-800 ${buttonColorClass} disabled:opacity-50`}
          >
            {uploading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-400 dark:border-gray-300 border-t-transparent"></div>
                Uploading...
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Image
              </>
            )}
          </button>
          {imageUrl && onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="ml-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              Remove
            </button>
          )}
          {helpText && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {helpText}
            </p>
          )}
          {error && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

